import { type ProductGroup } from "../models";
import { type ProductGroupDTO } from "../dto";
import { SearchPageType } from "../useSearchPageType";

import { toMapFn } from "~/utils/reducers";
import type { FacetDTO, FacetValueDTO } from "../dto/SearchResultsDTO";

type VisibilityPredicate = (category: ProductGroupDTO, prefix?: string) => boolean | undefined;

export function getCategoryVisibilityFunction(
  facet: FacetDTO | undefined,
  pageType: SearchPageType | undefined,
): VisibilityPredicate {
  if (pageType === SearchPageType.SELECTION_ASSISTANT) {
    return (category: ProductGroupDTO) => category.isVisibleForSA;
  }
  const visibleGroups = toMapFn(
    facet?.values ?? [],
    (value: FacetValueDTO) => value.code,
    () => true,
  );
  return (category: ProductGroupDTO, prefix = "") => visibleGroups[prefix + "/" + category.code] ?? false;
}

export function transformCategoryPaths(data: ProductGroupDTO[], isCategoryVisible: VisibilityPredicate) {
  const groups = mergeTopLevel(transform(data, "", isCategoryVisible));
  return window.SCHA.config.isZoovuEnabled ? groups : sortGroups(groups);
}

// This function will merge top level ProductGroup elements with their immediate subGroups, so we can create groups with
// ids like /ROTARY/RollingAndPlainBearings from /ROTARY and RollingAndPlainBearings
function mergeTopLevel(taxonomy: ProductGroup[]): ProductGroup[] {
  return taxonomy.flatMap((item) =>
    item.children.map((subItem) => ({
      ...subItem,
      fullFacetUid: "/" + item.treeNodeUid + "/" + subItem.treeNodeUid,
    })),
  );
}

function transform(
  list: ProductGroupDTO[],
  fullCodePrefix: string,
  isCategoryVisibile: VisibilityPredicate,
): ProductGroup[] {
  return list
    .filter((item) => isCategoryVisibile(item, fullCodePrefix))
    .map((item) => transformItem(item, fullCodePrefix, isCategoryVisibile));
}

function transformItem(
  dto: ProductGroupDTO,
  fullCodePrefix: string,
  isCategoryVisibile: VisibilityPredicate,
): ProductGroup {
  const fullCode = fullCodePrefix + "/" + dto.code;
  return {
    treeNodeUid: dto.code,
    name: dto.name,
    fullFacetUid: fullCode,
    children: transform(dto.subcategories, fullCode, isCategoryVisibile),
    description: dto.description,
    order: dto.order ?? Number.MAX_SAFE_INTEGER,
  };
}

function sortGroups(groups: ProductGroup[]) {
  groups.forEach((group) => sortGroups(group.children));

  return groups.sort((a, b) => {
    const orderSort = a.order - b.order;

    return orderSort !== 0 ? orderSort : a.name?.localeCompare(b.name);
  });
}
